/** Remembers how far the page's scroll area was scrolled for each history entry, bounded so it cannot grow without end. */
export interface ScrollMemory {
  /** Records `top` for an entry; the entry becomes the most recent. */
  save(entry: string, top: number): void
  /** The remembered offset for an entry, if it is still held. */
  recall(entry: string): number | undefined
}

/** Names one history entry: the router's position in the stack plus the location held there. */
export function historyEntryKey(position: number | null | undefined, fullPath: string): string {
  return `${typeof position === 'number' ? position : 'x'}|${fullPath}`
}

/** A scroll memory that keeps at most `capacity` entries, dropping the least recently saved. */
export function createScrollMemory(capacity = 64): ScrollMemory {
  const offsets = new Map<string, number>()
  return {
    save(entry, top) {
      offsets.delete(entry)
      offsets.set(entry, Math.max(0, Math.round(top)))
      while (offsets.size > capacity) {
        const oldest = offsets.keys().next()
        if (oldest.done) break
        offsets.delete(oldest.value)
      }
    },
    recall(entry) {
      return offsets.get(entry)
    },
  }
}

/** What to do with the page's scroll area once a navigation has landed. */
export type ScrollPlan =
  | { kind: 'keep' }
  | { kind: 'top' }
  | { kind: 'restore'; top: number }
  | { kind: 'hash'; hash: string }

/** Chooses the scroll for a landed navigation: a history pop restores, a hash scrolls to it, a new page starts at the top, a same-page change stays put. */
export function planScroll(input: {
  pageChanged: boolean
  popped: boolean
  hash: string
  remembered: number | undefined
}): ScrollPlan {
  if (input.popped && input.remembered !== undefined) {
    return { kind: 'restore', top: input.remembered }
  }
  if (input.hash) return { kind: 'hash', hash: input.hash }
  if (input.pageChanged) return { kind: 'top' }
  return { kind: 'keep' }
}
